import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../utils/api';
import { callAuthPostWithFallback } from '../utils/authFallback';
import { validateUserRole, normalizeRole, getEmergencyAccess } from '../utils/roleMapping';
import { isTestToken, autoFixTestToken, extractUserFromToken } from '../utils/jwtTokenGenerator';
import { NOTIFICATION_DEFAULTS, normalizeNotificationPreferences } from '../constants/notificationPreferences';

export const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const SANDBOX_API_BASE = 'https://zbxepuqat0.execute-api.eu-west-2.amazonaws.com/sandbox';
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notificationPreferences, setNotificationPreferences] = useState(() => {
    try {
      const stored = localStorage.getItem('notification_preferences');
      if (stored) {
        const parsed = JSON.parse(stored);
        return normalizeNotificationPreferences(parsed);
      }
    } catch (storageError) {
      console.warn('AuthContext: Failed to parse stored notification preferences', storageError);
    }
    return { ...NOTIFICATION_DEFAULTS };
  });
  const [notificationDelivery, setNotificationDelivery] = useState({
    email: true,
    inApp: true,
    push: false
  });

  // Check authentication status on mount
  useEffect(() => {
    checkAuthStatus();
  }, []); // Only run on mount
  
  // Set up retry mechanism for server errors (separate effect)
  useEffect(() => {
    // Set up retry mechanism for server errors
    // If server is down, retry every 10 seconds
    const retryInterval = setInterval(() => {
      const token = localStorage.getItem('token');
      // Only retry if we have a token but no user (indicates server error)
      if (token && !user && !loading) {
        console.log('🔄 Retrying auth check (server might have been down)...');
        checkAuthStatus();
      }
    }, 10000); // Retry every 10 seconds
    
    return () => clearInterval(retryInterval);
  }, [user, loading]); // Dependencies for retry logic only

  const refreshNotificationPreferences = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setNotificationPreferences({ ...NOTIFICATION_DEFAULTS });
        setNotificationDelivery({
          email: true,
          inApp: true,
          push: false
        });
        return;
      }

      const response = await api.get('/users/notification-preferences');
      if (response.data?.success) {
        const normalized = normalizeNotificationPreferences(response.data.data || {});
        setNotificationPreferences(normalized);
        localStorage.setItem('notification_preferences', JSON.stringify(normalized));

        const delivery = response.data.deliveryMethods || {};
        setNotificationDelivery({
          email: delivery.email !== false,
          inApp: delivery.inApp !== false,
          push: delivery.push === true
        });
      }
    } catch (preferencesError) {
      console.warn('AuthContext: Failed to refresh notification preferences', preferencesError);
    }
  };

  const checkAuthStatus = async () => {
    try {
      let token = localStorage.getItem('token');
      
      // CRITICAL DEBUG: Decode and inspect JWT token
      if (token) {
        try {
          const base64Url = token.split('.')[1];
          const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
          const jsonPayload = decodeURIComponent(window.atob(base64).split('').map(c => {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
          }).join(''));
          const tokenPayload = JSON.parse(jsonPayload);
          
          console.log('🔐 JWT TOKEN INSPECTION:', {
            userId: tokenPayload.userId,
            email: tokenPayload.email,
            role: tokenPayload.role,
            exp: new Date(tokenPayload.exp * 1000).toLocaleString()
          });
        } catch (decodeError) {
          console.error('❌ Failed to decode JWT token:', decodeError);
        }
      }
      
      // REMOVED: auth_mismatch flag check - backend is now fixed to return correct user
      // No need for aggressive logout checks since the root cause is eliminated
      
      // Check if we have a test token and try to fix it
      if (token && isTestToken(token)) {
        console.warn('🚨 AuthContext: Found test token in localStorage, attempting to fix');
        
        // Try to extract user data from the token or use fallback
        const userFromToken = extractUserFromToken(token);
        if (userFromToken) {
          const realToken = autoFixTestToken(userFromToken);
          if (realToken) {
            token = realToken;
            console.log('✅ AuthContext: Test token replaced with real JWT token');
          }
        } else {
          // If we can't extract user data, remove the test token
          console.warn('⚠️ AuthContext: Removing invalid test token');
          localStorage.removeItem('token');
          setUser(null);
          setLoading(false);
          return;
        }
      }
      
      if (token) {
        try {
          // SECURITY: Store expected user email if we have one from previous session
          const expectedUserEmail = localStorage.getItem('expected_user_email');
          
          // First try to get user profile (for regular users)
          let response;
          let userData;
          
          try {
            console.log('🔐 SECURITY: Fetching /users/profile on page refresh');
            response = await api.get('/users/profile');
            userData = response.data.user || response.data; // Extract user from response
            
            console.log('🔐 SECURITY: Profile response:', {
              email: userData.email,
              id: userData.id,
              name: `${userData.firstName} ${userData.lastName}`,
              company: userData.companyName
            });
            
            // SECURITY MONITORING: Log user identity for debugging (NO FORCED LOGOUT)
            // Backend now returns correct user, so no need for aggressive logout
            if (expectedUserEmail && userData.email) {
              const normalizedExpected = String(expectedUserEmail).trim().toLowerCase();
              const normalizedActual = String(userData.email).trim().toLowerCase();
              
              if (normalizedExpected && normalizedActual && normalizedExpected !== normalizedActual) {
                // Log mismatch for monitoring, but DON'T force logout
                console.warn(`⚠️ SECURITY MONITORING: User email changed from ${normalizedExpected} to ${normalizedActual}`);
                console.warn('This could be a legitimate re-login. Allowing user to continue.');
              } else if (normalizedExpected === normalizedActual) {
                console.log(`✅ User identity verified: ${userData.email}`);
              }
            }
            
            // Store the user email for future verification (only if it exists)
            if (userData.email) {
              localStorage.setItem('expected_user_email', userData.email);
              console.log(`🔐 Session verified for: ${userData.email}`);
            }
            
          } catch (userError) {
            // If user profile fails, try staff profile
            try {
              response = await api.get('/api/staff/profile');
              userData = response.data.user || response.data; // Extract user from response
              
              // SECURITY: Log staff user identity for monitoring (NOT ENFORCED - just logging)
              if (expectedUserEmail && userData.email) {
                const normalizedExpected = String(expectedUserEmail).trim().toLowerCase();
                const normalizedActual = String(userData.email).trim().toLowerCase();
                
                if (normalizedExpected && normalizedActual && normalizedExpected !== normalizedActual) {
                  // Log the mismatch but DON'T force logout - just monitor
                  console.warn(`⚠️ SECURITY MONITORING (Staff): User identity changed from ${normalizedExpected} to ${normalizedActual}`);
                  console.warn('This could indicate a session issue but allowing user to continue.');
                } else if (normalizedExpected === normalizedActual) {
                  console.log(`✅ Staff user identity consistent: ${userData.email}`);
                }
              }
            } catch (staffError) {
              // Check if it's an auth error (401, 403) or a server/network error (500, timeout, etc.)
              const isAuthError = 
                userError.response?.status === 401 || 
                userError.response?.status === 403 ||
                staffError.response?.status === 401 || 
                staffError.response?.status === 403;
              
              const isServerError = 
                userError.response?.status >= 500 || 
                staffError.response?.status >= 500 ||
                !userError.response || 
                !staffError.response;
              
              if (isAuthError) {
                // Token is actually invalid - remove it
                console.error('AuthContext: Auth error (401/403) - removing token');
                localStorage.removeItem('token');
                setUser(null);
                setLoading(false);
                return;
              } else if (isServerError) {
                // Server error or network issue - keep token and retry later
                console.warn('AuthContext: Server/network error - keeping token for retry');
                console.warn('User error:', userError.response?.status, 'Staff error:', staffError.response?.status);
                // Do not clear existing user data to avoid UI flicker if we already have profile cached
                setLoading(false);
                return;
              } else {
                // Unknown error - log but keep token
                console.error('AuthContext: Both user and staff profile calls failed with unknown error');
                console.error('User error:', userError.response?.status, 'Staff error:', staffError.response?.status);
                setUser(null);
                setLoading(false);
                return;
              }
            }
          }
          
          // DEBUG: Log user data for troubleshooting
          console.log('🔍 AuthContext: Raw user data from API:', userData);
          console.log('🔍 AuthContext: Company name:', userData.companyName);
          console.log('🔍 AuthContext: Subscription:', { 
            plan: userData.subscriptionPlan, 
            status: userData.subscriptionStatus 
          });
          console.log('🔍 AuthContext: Address data:', {
            address: userData.address,
            city: userData.city,
            state: userData.state,
            zipCode: userData.zipCode,
            country: userData.country
          });
          
          // Validate and normalize user role
          const validatedUser = validateUserRole(userData);
          
          // Log role validation for debugging
          if (validatedUser.originalRole !== validatedUser.role) {
            console.info('🔄 AuthContext: Role normalized:', validatedUser.originalRole, '→', validatedUser.role);
          }
          
          // Check for emergency fallback
          const emergencyAccess = getEmergencyAccess(validatedUser.role);
          if (emergencyAccess.isEmergencyFallback) {
            console.warn('🚨 AuthContext: Emergency fallback activated for user:', validatedUser.email, 'with role:', validatedUser.originalRole);
          }
          
          // CRITICAL SECURITY: Log what user we're about to set
          console.log('🔐 SECURITY: Setting user state to:', {
            email: validatedUser.email,
            id: validatedUser.id,
            name: `${validatedUser.firstName} ${validatedUser.lastName}`,
            company: validatedUser.companyName,
            role: validatedUser.role,
            address: validatedUser.address,
            city: validatedUser.city,
            zipCode: validatedUser.zipCode,
            country: validatedUser.country
          });
          
          setUser(validatedUser);
          refreshNotificationPreferences();
          
          // Set appropriate active tab based on user role
          if (validatedUser.canAccessStaffPortal) {
            localStorage.setItem('staffPortalActiveTab', 'dashboard');
          } else {
            localStorage.setItem('clientPortalActiveTab', 'dashboard');
          }
        } catch (apiError) {
          console.error('AuthContext: API error during auth check:', apiError);
          setUser(null);
        }
      } else {
        setUser(null);
      }
    } catch (error) {
      console.error('AuthContext: Auth check failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const completeLoginSession = async ({ token: initialToken, userData, loginEmail }) => {
    let token = initialToken;

    if (!token || !userData) {
      throw new Error('Missing token or user data');
    }

    if (loginEmail && userData.email && userData.email.toLowerCase() !== loginEmail.toLowerCase()) {
      throw new Error('Security validation failed: email mismatch');
    }

    if (isTestToken(token)) {
      console.warn('🚨 AuthContext: Detected test token, generating real JWT token');
      const realToken = autoFixTestToken(userData);
      if (realToken) {
        token = realToken;
        console.log('✅ AuthContext: Test token replaced with real JWT token');
      } else {
        console.error('❌ AuthContext: Failed to generate real JWT token');
      }
    }

    localStorage.setItem('token', token);

    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(window.atob(base64).split('').map(c => {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      const tokenPayload = JSON.parse(jsonPayload);

      console.log('🔐 JWT TOKEN STORED AFTER LOGIN:', {
        loginEmail,
        tokenUserId: tokenPayload.userId,
        tokenEmail: tokenPayload.email,
        tokenRole: tokenPayload.role,
        MATCH: loginEmail ? tokenPayload.email?.toLowerCase() === loginEmail?.toLowerCase() : 'N/A'
      });

      if (loginEmail && tokenPayload.email?.toLowerCase() !== loginEmail?.toLowerCase()) {
        localStorage.clear();
        throw new Error('Backend returned incorrect user token');
      }
    } catch (decodeError) {
      console.error('❌ Failed to decode login JWT token:', decodeError);
    }

    const validatedUser = validateUserRole(userData);
    let finalUser = validatedUser;

    // Hydrate with the latest profile data to capture address/company info immediately after login
    try {
      const profileResponse = await api.get('/users/profile', { __skipAuthTokenPurge: true });
      const profileUser = profileResponse.data?.user || profileResponse.data;
      if (profileUser?.id) {
        finalUser = validateUserRole(profileUser);
      }
    } catch (profileError) {
      console.warn('AuthContext: Unable to hydrate client profile after login, trying staff profile...', profileError?.message || profileError);
      try {
        const staffProfileResponse = await api.get('/api/staff/profile', { __skipAuthTokenPurge: true });
        const staffUser = staffProfileResponse.data?.user || staffProfileResponse.data;
        if (staffUser?.id) {
          finalUser = validateUserRole(staffUser);
        }
      } catch (staffProfileError) {
        console.warn('AuthContext: Staff profile hydration skipped:', staffProfileError?.message || staffProfileError);
      }
    }

    localStorage.setItem('expected_user_email', finalUser.email);
    localStorage.removeItem('auth_mismatch');
    console.log(`🔐 SECURITY: Session established for ${finalUser.email}`);

    if (finalUser.originalRole !== finalUser.role) {
      console.info('🔄 AuthContext: Role normalized:', finalUser.originalRole, '→', finalUser.role);
    }

    const emergencyAccess = getEmergencyAccess(finalUser.role);
    if (emergencyAccess.isEmergencyFallback) {
      console.warn('🚨 AuthContext: Emergency fallback activated during login for user:', finalUser.email, 'with role:', finalUser.originalRole);
    }

    setUser(finalUser);

    if (finalUser.canAccessStaffPortal) {
      localStorage.setItem('staffPortalActiveTab', 'dashboard');
    } else {
      localStorage.setItem('clientPortalActiveTab', 'dashboard');
    }

    return finalUser;
  };

  const login = async (email, password) => {
    try {
      setLoading(true);
      setError(null);
      
      // CRITICAL SECURITY: Clear ALL localStorage before login to prevent cross-user contamination
      console.log('🔐 SECURITY: Clearing all localStorage before login');
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key) keysToRemove.push(key);
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
      console.log(`🔐 SECURITY: Cleared ${keysToRemove.length} localStorage keys`);

      const isProductionHost =
        typeof window !== 'undefined' &&
        window.location?.hostname &&
        window.location.hostname.includes('receevit.com') &&
        !window.location.hostname.includes('sandbox');

      const loginViaApi = async () => {
        console.log('🔐 AuthContext: Initiating login via API proxy');
        const response = await api.post('/api/auth/login', { email, password });
        return response.data;
      };

      const loginViaLambda = async () => {
        if (isProductionHost) {
          throw new Error('Lambda fallback disabled on production host');
        }
        console.log('🔐 AuthContext: Initiating login via Lambda fallback');
        return await callAuthPostWithFallback(api, {
          apiPath: '/api/auth/login',
          fallbackPath: '/api/auth/login',
          payload: { email, password },
          method: 'post',
          requireAuthToken: false,
          forceFallback: true,
          fallbackBaseUrlOverride: SANDBOX_API_BASE,
        });
      };

      let loginResponse;
      let usedFallback = false;

      try {
        if (isProductionHost) {
          loginResponse = await loginViaApi();
        } else {
          loginResponse = await loginViaLambda();
          usedFallback = true;
        }
      } catch (lambdaError) {
        console.warn('🔐 AuthContext: Lambda login failed, attempting API proxy', lambdaError);
        loginResponse = await loginViaApi();
      }

      console.log('🔐 AuthContext: Login response (initial)', loginResponse, 'usedFallback:', usedFallback);

      if (
        !usedFallback &&
        loginResponse?.success &&
        loginResponse?.user?.twoFactorEnabled &&
        !loginResponse?.requiresTwoFactor
      ) {
        console.warn('🔐 AuthContext: 2FA enabled user returned without challenge – retrying via Lambda');
        try {
          const fallbackResponse = await loginViaLambda();
          if (fallbackResponse?.success) {
            loginResponse = fallbackResponse;
            usedFallback = true;
            console.log('🔐 AuthContext: Lambda fallback succeeded after missing 2FA challenge');
          }
        } catch (fallbackError) {
          console.error('🔐 AuthContext: Lambda fallback login failed', fallbackError);
        }
      }

      console.log('🔐 AuthContext: Final login response', loginResponse, 'usedFallback:', usedFallback);
      console.log('🔐 AuthContext: Response user.twoFactorEnabled =', loginResponse?.user?.twoFactorEnabled);

      if (usedFallback && !isProductionHost) {
        localStorage.setItem('__sandboxApiBaseOverride', SANDBOX_API_BASE);
      } else {
        localStorage.removeItem('__sandboxApiBaseOverride');
      }

      if (loginResponse?.requiresTwoFactor) {
        console.log('🔐 Two-factor authentication required for', email);
        return {
          success: true,
          requiresTwoFactor: true,
          twoFactorToken: loginResponse.twoFactorToken,
          user: loginResponse.user
        };
      }

      // Handle both success: true and success: false with 'Login successful' message
      const isSuccessful = loginResponse?.success || 
        (loginResponse?.message && loginResponse.message.includes('Login successful'));
      
      if (isSuccessful) {
        const userData = loginResponse.user;
        const validatedUser = await completeLoginSession({ token: loginResponse.token, userData, loginEmail: email });
        return { success: true, user: validatedUser };
      } else {
        const message = loginResponse?.message || 'Login failed';
        setError(message);
        return { success: false, error: message };
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Login failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const register = async (userData) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.post('/api/auth/register', userData);
      
      if (response.data.success) {
        // Registration successful, but user needs to complete payment
        return { 
          success: true, 
          user: response.data.user,
          checkoutUrl: response.data.checkoutUrl,
          requiresPayment: response.data.requiresPayment
        };
      } else {
        setError(response.data.message || 'Registration failed');
        return { success: false, error: response.data.message };
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Registration failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const staffLogin = async (email, password) => {
    try {
      setLoading(true);
      setError(null);
      
      // Use the same login endpoint - the backend will handle staff authentication
      const response = await api.post('/api/auth/login', { email, password });
      
      const isSuccessful = response.data.success || 
        (response.data.message && response.data.message.includes('Login successful'));
      
      if (isSuccessful) {
        let token = response.data.token;
        let userData = response.data.user;
        
        // Check if user has staff/admin role
        if (!userData.role || (userData.role !== 'admin' && userData.role !== 'staff')) {
          setError('Access denied. Staff credentials required.');
          return { success: false, error: 'Access denied. Staff credentials required.' };
        }
        
        // Check if we received a test token and auto-fix it
        if (isTestToken(token)) {
          console.warn('🚨 AuthContext: Detected test token, generating real JWT token');
          const realToken = autoFixTestToken(userData);
          if (realToken) {
            token = realToken;
            console.log('✅ AuthContext: Test token replaced with real JWT token');
          }
        }
        
        localStorage.setItem('token', token);
        
        // Validate and normalize user role
        const validatedUser = validateUserRole(userData);
        
        // Log role validation for debugging
        if (validatedUser.originalRole !== validatedUser.role) {
          console.info('🔄 AuthContext: Staff login role normalized:', validatedUser.originalRole, '→', validatedUser.role);
        }
        
        setUser(validatedUser);
        
        // Set staff portal active tab
        localStorage.setItem('staffPortalActiveTab', 'dashboard');
        
        return { success: true, user: validatedUser };
      } else {
        setError(response.data.message || 'Staff login failed');
        return { success: false, error: response.data.message };
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Staff login failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    // CRITICAL SECURITY: Clear ALL localStorage to prevent cross-user contamination
    console.log('🔐 SECURITY: Logging out and clearing ALL localStorage');
    localStorage.clear();
    setUser(null);
    setError(null);
    setNotificationPreferences({ ...NOTIFICATION_DEFAULTS });
    setNotificationDelivery({
      email: true,
      inApp: true,
      push: false
    });
    console.log('✅ SECURITY: Logout complete - all data cleared');
  };

  const isAuthenticated = !!user;

  const updateProfile = async (updates = {}) => {
    return new Promise(resolve => {
      setUser(prev => {
        if (!prev || typeof updates !== 'object' || updates === null) {
          resolve(prev);
          return prev;
        }

        const next = {
          ...prev,
          ...updates
        };

        resolve(next);
        return next;
      });
    });
  };

  const value = {
    user,
    loading,
    error,
    login,
    completeLoginSession,
    staffLogin,
    register,
    logout,
    isAuthenticated,
    setError,
    notificationPreferences,
    setNotificationPreferences,
    notificationDelivery,
    setNotificationDelivery,
    refreshNotificationPreferences,
    updateProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};